import { useState } from "react";
import { Link } from "react-router-dom";
import { C } from "../constants/theme";
import { useSiteContent } from "../context/SiteContentContext";
import FadeIn from "../components/ui/FadeIn";
import TiltCard from "../components/ui/TiltCard";
import RainbowDivider from "../components/ui/RainbowDivider";

const PACKS = [
  { icon:"📝", title:"Worksheets",       body:"Print-and-go practice sheets across subjects." },
  { icon:"🗂️", title:"Flashcards",       body:"Bite-sized recall cards for letters, words, and concepts." },
  { icon:"🧩", title:"Matching Cards",   body:"Hands-on pairing games for early learners." },
  { icon:"📓", title:"Busy Books",       body:"Quiet-time activity books for independent play." },
  { icon:"📁", title:"Learning Folders", body:"Organised take-home folders for ongoing topics." },
  { icon:"🗓️", title:"Visual Schedules", body:"Picture-based daily routines for young children." },
  { icon:"🎲", title:"Educational Games", body:"Playful games that reinforce what's being learned." },
];

const WHATSAPP_NUMBER = "923390002106";

const Resources = () => {
  const { content } = useSiteContent();
  const ct = content.contact;
  const whatsappNumber = (ct?.whatsappLink || `https://wa.me/${WHATSAPP_NUMBER}`).replace(/\D/g, "").slice(-12) || WHATSAPP_NUMBER;
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [designation, setDesignation] = useState("");
  const [source, setSource] = useState("");
  const [triedIncomplete, setTriedIncomplete] = useState(false);

  const toggle = (title) =>
    setSelected(s => s.includes(title) ? s.filter(t => t !== title) : [...s, title]);

  const isComplete = name.trim() && phone.trim() && designation && source;

  const items = () => selected.length ? selected.join(", ") : "(not specified — please advise what's available)";

  const waLink = () => {
    const text = `Assalamu Alaikum! I'd like to be notified when paid resource packs are available from Mini Muslims Nest.\nName: ${name.trim()}\nPhone: ${phone.trim()}\nEmail: ${email.trim() || "(not provided)"}\nI am a: ${designation}\nFound you via: ${source}\nInterested in: ${items()}.`;
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(text)}`;
  };

  const handleSend = () => {
    if (!isComplete) { setTriedIncomplete(true); return; }
    window.open(waLink(), "_blank", "noopener,noreferrer");
  };

  return (
    <div style={{ paddingTop: 72 }}>
      {/* ── Hero ── */}
      <section style={{ background:`linear-gradient(160deg,${C.yellow} 0%,#d89400 100%)`, padding:"100px 6vw 90px", position:"relative", overflow:"hidden", minHeight:"46vh", display:"flex", alignItems:"center" }}>
        <div style={{ position:"absolute", top:-80, right:-80, width:400, height:400, borderRadius:"50%", background:"rgba(255,255,255,0.1)", pointerEvents:"none" }}/>
        <div style={{ maxWidth:1100, margin:"0 auto", position:"relative", zIndex:2 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(255,255,255,0.2)", color:C.navy, fontFamily:"Nunito", fontWeight:800, fontSize:11, letterSpacing:"0.15em", padding:"6px 16px", borderRadius:100, textTransform:"uppercase", marginBottom:20 }}>Coming Soon</div>
          <h1 style={{ fontFamily:"Fredoka One", fontSize:"clamp(28px,5vw,52px)", color:C.navy, lineHeight:1.1, margin:"0 0 18px", maxWidth:640 }}>
            Get Notified When Paid Resources Are Added.
          </h1>
          <p style={{ fontFamily:"Lora", fontStyle:"italic", fontSize:"clamp(14px,2vw,18px)", color:"#5a4200", maxWidth:580, lineHeight:1.8, margin:0 }}>
            We're building out printable resource packs — worksheets, flashcards, busy books, and more — for families who'd like extra support at home. Tell us what you're interested in and we'll let you know the moment they're ready.
          </p>
        </div>
      </section>

      {/* ── Resource packs ── */}
      <section style={{ background:C.cream, padding:"90px 6vw" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center", marginBottom:48 }}>
              <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.coral, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>What's Coming</div>
              <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(24px,3.5vw,40px)", color:C.navy, margin:0 }}>Our Upcoming Resource Packs.</h2>
              <RainbowDivider />
            </div>
          </FadeIn>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:18 }}>
            {PACKS.map(({ icon, title, body }, i) => (
              <FadeIn key={title} delay={i * 0.05}>
                <TiltCard maxTilt={4} onClick={() => toggle(title)} style={{
                  background:C.white, borderRadius:18, padding:"22px 20px", cursor:"pointer",
                  border: selected.includes(title) ? `2px solid ${C.yellow}` : `2px solid ${C.navy}12`,
                  boxShadow: selected.includes(title) ? `0 6px 18px ${C.yellow}40` : "0 3px 12px rgba(0,0,0,0.04)",
                  transition:"border 0.2s, box-shadow 0.2s",
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div style={{ fontSize:28, marginBottom:10 }}>{icon}</div>
                    <div style={{
                      width:20, height:20, borderRadius:6, flexShrink:0,
                      border:`2px solid ${selected.includes(title) ? C.yellow : C.navy}30`,
                      background: selected.includes(title) ? C.yellow : "transparent",
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:C.navy,
                    }}>{selected.includes(title) ? "✓" : ""}</div>
                  </div>
                  <div style={{ fontFamily:"Fredoka One", fontSize:16, color:C.navy, marginBottom:6 }}>{title}</div>
                  <p style={{ fontFamily:"Nunito", fontSize:12.5, color:C.muted, lineHeight:1.6, margin:0 }}>{body}</p>
                </TiltCard>
              </FadeIn>
            ))}
          </div>
          <p style={{ textAlign:"center", fontFamily:"Nunito", fontStyle:"italic", fontSize:13, color:C.muted, marginTop:24 }}>
            Tap a pack to tell us you're interested, then send your details below. Looking for something else — including books? Mention it in your message; our catalogue is always growing.
          </p>
        </div>
      </section>

      {/* ── Order via WhatsApp ── */}
      <section style={{ background:C.warmGray, padding:"90px 6vw" }}>
        <div style={{ maxWidth:560, margin:"0 auto" }}>
          <FadeIn>
            <div style={{ background:C.white, borderRadius:24, padding:"40px 36px", boxShadow:"0 8px 32px rgba(0,0,0,0.07)", textAlign:"center" }}>
              <div style={{ fontSize:40, marginBottom:14 }}>🔔</div>
              <h2 style={{ fontFamily:"Fredoka One", fontSize:22, color:C.navy, margin:"0 0 8px" }}>Get Notified — Tell Us You're Interested</h2>
              <p style={{ fontFamily:"Nunito", fontSize:13.5, color:C.muted, lineHeight:1.7, margin:"0 0 24px" }}>
                Select your packs above, fill in your details below, then send — it opens WhatsApp with your message pre-filled so nothing gets lost.
              </p>

              <input
                type="text" placeholder="Your Full Name *" value={name} onChange={e=>setName(e.target.value)}
                style={{
                  width:"100%", padding:"12px 15px", border:`2px solid ${C.navy}18`, borderRadius:12,
                  fontFamily:"Nunito", fontSize:14, color:C.text, background:C.white, outline:"none",
                  boxSizing:"border-box", marginBottom:12,
                }}
              />
              <input
                type="tel" placeholder="Your Phone / WhatsApp Number *" value={phone} onChange={e=>setPhone(e.target.value)}
                style={{
                  width:"100%", padding:"12px 15px", border:`2px solid ${C.navy}18`, borderRadius:12,
                  fontFamily:"Nunito", fontSize:14, color:C.text, background:C.white, outline:"none",
                  boxSizing:"border-box", marginBottom:12,
                }}
              />
              <input
                type="email" placeholder="Your Email (optional)" value={email} onChange={e=>setEmail(e.target.value)}
                style={{
                  width:"100%", padding:"12px 15px", border:`2px solid ${C.navy}18`, borderRadius:12,
                  fontFamily:"Nunito", fontSize:14, color:C.text, background:C.white, outline:"none",
                  boxSizing:"border-box", marginBottom:16,
                }}
              />

              <div style={{ textAlign:"left", marginBottom:16 }}>
                <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.navy, marginBottom:8 }}>I am a... *</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {["Mother","Teacher","Other"].map(val => (
                    <button key={val} type="button" onClick={()=>setDesignation(val)} style={{
                      background: designation === val ? C.navy : C.white,
                      color: designation === val ? C.white : C.navy,
                      border:`2px solid ${C.navy}`, fontFamily:"Nunito", fontWeight:700, fontSize:12.5,
                      padding:"7px 14px", borderRadius:100, cursor:"pointer", transition:"all 0.2s",
                    }}>{val}</button>
                  ))}
                </div>
              </div>

              <div style={{ textAlign:"left", marginBottom:20 }}>
                <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.navy, marginBottom:8 }}>Where did you find us? *</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {["Facebook","Instagram","Google","Other"].map(val => (
                    <button key={val} type="button" onClick={()=>setSource(val)} style={{
                      background: source === val ? C.navy : C.white,
                      color: source === val ? C.white : C.navy,
                      border:`2px solid ${C.navy}`, fontFamily:"Nunito", fontWeight:700, fontSize:12.5,
                      padding:"7px 14px", borderRadius:100, cursor:"pointer", transition:"all 0.2s",
                    }}>{val}</button>
                  ))}
                </div>
              </div>

              <div style={{ fontFamily:"Nunito", fontSize:12.5, color:C.muted, marginBottom:18, textAlign:"left" }}>
                Interested in: {selected.length ? selected.join(", ") : "none yet — tap packs above (you can still send without selecting)"}
              </div>

              <button type="button" onClick={handleSend} style={{
                display:"block", width:"100%", background: isComplete ? "#25D366" : `${C.navy}30`,
                color: isComplete ? C.white : C.navy, border:"none",
                fontFamily:"Nunito", fontWeight:800, fontSize:15, padding:"15px", borderRadius:100, cursor:"pointer",
              }}>🔔 Notify Me via WhatsApp</button>
              {triedIncomplete && !isComplete && (
                <p style={{ fontFamily:"Nunito", fontSize:12, color:C.coral, marginTop:10 }}>
                  Please fill in your name, phone number, designation, and how you found us.
                </p>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ background:C.cream, padding:"70px 6vw" }}>
        <div style={{ maxWidth:560, margin:"0 auto", textAlign:"center" }}>
          <FadeIn>
            <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(20px,3vw,30px)", color:C.navy, margin:"0 0 14px" }}>Also Explore</h2>
            <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
              <Link to="/free-resources" style={{ background:C.mint, color:C.white, textDecoration:"none", fontFamily:"Nunito", fontWeight:700, fontSize:13.5, padding:"12px 26px", borderRadius:100 }}>Free Resources</Link>
              <Link to="/upcoming" style={{ background:"transparent", color:C.navy, textDecoration:"none", fontFamily:"Nunito", fontWeight:700, fontSize:13.5, padding:"12px 26px", borderRadius:100, border:`2px solid ${C.navy}` }}>Upcoming Courses</Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default Resources;
